import { useEffect, useMemo, useState } from 'react';
import { getAircraft, getFinanceOwners, getFlights } from '../../api';
import type {
  Flight,
  MaintenanceCostSplit,
  MaintenanceOwnerShare,
  MaintenanceRecord,
} from '../../types';
import { DateFormat } from '../../utils/DateFormat';
import {
  formatPerformerLabel,
  performerFromAircraft,
  performerFromLabel,
  performerNamePlaceholder,
  type MaintenancePerformerType,
} from '../../utils/aircraftMaintenance';
import { getApiErrorMessage } from '../../utils/apiError';
import {
  manualShareError,
  manualSharePairs,
  sharesFromAllocations,
} from '../../utils/expenseSplit';
import {
  flightDurationMh,
  flightMotohoursEnd as motoOf,
  flightTachValue,
  MH_UNIT,
} from '../../utils/flightDisplay';
import { parseOptionalCost, resolveCostSplit } from '../../utils/maintenanceCost';
import { useFeedback } from '../../hooks/useFeedback';
import MaintenanceCostFields from '../MaintenanceCostFields/MaintenanceCostFields';
import ModalPortal from '../modal/ModalPortal';
import './PerformMaintenanceModal.css';

type MaintenanceReason = 'initial' | 'hours' | 'year';

export type PerformMaintenancePayload = {
  after_flight_id?: number;
  done_date: string;
  reason?: MaintenanceReason;
  mechanic_name?: string;
  cost?: number;
  cost_split?: MaintenanceCostSplit;
  payer_ids?: number[];
  payer_shares?: number[];
  info?: string;
  clear_cost?: boolean;
};

type Props = {
  title: 'ТО-50' | 'ТО-100';
  lastDoneHobbs?: number;
  maxHobbs?: number;
  submitting?: boolean;
  mode?: 'create' | 'edit';
  initialFlightId?: number | null;
  initialDoneDate?: string;
  initialReason?: MaintenanceReason | '';
  initialMechanic?: string;
  initialRecord?: MaintenanceRecord | null;
  onClose: () => void;
  onConfirm: (data: PerformMaintenancePayload) => void;
};

const toISODate = (value: string) => String(value).slice(0, 10);
const todayISO = () => new Date().toISOString().slice(0, 10);

const PerformMaintenanceModal = ({
  title,
  lastDoneHobbs = 0,
  maxHobbs,
  submitting = false,
  mode = 'create',
  initialFlightId = null,
  initialDoneDate = '',
  initialReason = '',
  initialMechanic = '',
  initialRecord = null,
  onClose,
  onConfirm,
}: Props) => {
  const { showError, feedbackNode } = useFeedback();
  const [flights, setFlights] = useState<Flight[]>([]);
  const [tipMoto, setTipMoto] = useState(0);
  const [hasAnyFlights, setHasAnyFlights] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [flightId, setFlightId] = useState<number | ''>('');
  const [doneDate, setDoneDate] = useState('');
  const [reason, setReason] = useState<MaintenanceReason | ''>('');
  const [performerType, setPerformerType] =
    useState<MaintenancePerformerType>('');
  const [performerName, setPerformerName] = useState('');
  const [cost, setCost] = useState(
    initialRecord?.cost != null && initialRecord.cost > 0
      ? String(initialRecord.cost)
      : ''
  );
  const [costSplit, setCostSplit] = useState<MaintenanceCostSplit>(
    resolveCostSplit(initialRecord)
  );
  const [manualShares, setManualShares] = useState<Record<number, string>>(() =>
    sharesFromAllocations(initialRecord?.pilot_data?.allocations)
  );
  const [info, setInfo] = useState(initialRecord?.info || '');
  const [owners, setOwners] = useState<MaintenanceOwnerShare[]>(
    initialRecord?.owners || []
  );

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setLoadFailed(false);
      try {
        const [flightsRes, aircraftRes] = await Promise.all([
          getFlights(),
          getAircraft().catch(() => null),
        ]);
        const performer = performerFromAircraft(aircraftRes?.data || null);
        setPerformerType(performer.type);
        setPerformerName(performer.name);
        const inWindow = (flight: Flight) => {
          const moto = motoOf(flight);
          if (moto <= lastDoneHobbs) return false;
          if (maxHobbs != null && moto >= maxHobbs) return false;
          return true;
        };
        const allFlights = flightsRes.data || [];
        setHasAnyFlights(allFlights.length > 0);
        setTipMoto(
          allFlights.reduce((max, flight) => Math.max(max, motoOf(flight)), 0)
        );
        const sorted = [...allFlights]
          .filter((flight) => inWindow(flight) || flight.id === initialFlightId)
          .sort((a, b) => {
            const motoDelta = motoOf(b) - motoOf(a);
            if (motoDelta !== 0) return motoDelta;
            return String(b.date).localeCompare(String(a.date));
          });
        setFlights(sorted);
        if (mode === 'edit') {
          const fromLabel = performerFromLabel(initialMechanic);
          if (fromLabel.type) {
            setPerformerType(fromLabel.type);
            setPerformerName(fromLabel.name);
          }
          const keep = initialFlightId
            ? sorted.find((f) => f.id === initialFlightId)
            : null;
          const fallback = sorted[0] || null;
          setFlightId(keep ? keep.id : fallback ? fallback.id : '');
          setDoneDate(
            keep
              ? initialDoneDate
                ? toISODate(initialDoneDate)
                : toISODate(String(keep.date))
              : fallback
                ? toISODate(String(fallback.date))
                : ''
          );
          setReason(
            title === 'ТО-100'
              ? (initialReason as MaintenanceReason) || 'hours'
              : ''
          );
        } else {
          const eligible = sorted;
          const initial = eligible[0] || null;
          if (initial) {
            setFlightId(initial.id);
            setDoneDate(toISODate(String(initial.date)));
            setReason(title === 'ТО-100' ? 'hours' : '');
          } else {
            setFlightId('');
            const starter =
              title === 'ТО-100' && lastDoneHobbs === 0 && sorted.length === 0;
            setDoneDate(starter ? todayISO() : '');
            setReason(starter ? 'initial' : title === 'ТО-100' ? 'hours' : '');
          }
        }
      } catch (error) {
        setFlights([]);
        setHasAnyFlights(false);
        setTipMoto(0);
        setFlightId('');
        setDoneDate('');
        setLoadFailed(true);
        showError(getApiErrorMessage(error, 'Не удалось загрузить полёты'));
      }
      setLoading(false);
    };
    load();
  }, [
    lastDoneHobbs,
    maxHobbs,
    showError,
    title,
    mode,
    initialFlightId,
    initialDoneDate,
    initialReason,
    initialMechanic,
  ]);

  useEffect(() => {
    if (!doneDate) {
      setOwners(initialRecord?.owners || []);
      return;
    }
    let cancelled = false;
    void getFinanceOwners(doneDate)
      .then((res) => {
        if (!cancelled) setOwners(res.data.owners || []);
      })
      .catch(() => {
        if (!cancelled) setOwners(initialRecord?.owners || []);
      });
    return () => {
      cancelled = true;
    };
  }, [doneDate, initialRecord]);

  const selected = useMemo(
    () => flights.find((f) => f.id === flightId) || null,
    [flights, flightId]
  );

  const eligibleFlights = flights;

  const starterWithoutFlights =
    title === 'ТО-100' &&
    lastDoneHobbs === 0 &&
    !loading &&
    !loadFailed &&
    !hasAnyFlights;

  const canSubmit =
    !submitting &&
    !loading &&
    !loadFailed &&
    (starterWithoutFlights || eligibleFlights.length > 0);

  const handleFlightChange = (id: number) => {
    setFlightId(id);
    const flight = flights.find((f) => f.id === id);
    if (flight) {
      setDoneDate(toISODate(String(flight.date)));
    }
  };

  const extras = (): Pick<
    PerformMaintenancePayload,
    'cost' | 'cost_split' | 'payer_ids' | 'payer_shares' | 'info' | 'clear_cost'
  > | null => {
    const parsed = parseOptionalCost(cost);
    if (parsed.error) {
      showError(parsed.error);
      return null;
    }
    if (parsed.cost != null && costSplit === 'manual') {
      const shareError = manualShareError(manualShares);
      if (shareError) {
        showError(shareError);
        return null;
      }
    }
    const pairs = manualSharePairs(manualShares);
    const hadCost = (initialRecord?.cost || 0) > 0;
    return {
      cost: parsed.cost ?? undefined,
      cost_split: parsed.cost != null || hadCost ? costSplit : undefined,
      info: info.trim(),
      clear_cost: parsed.cost == null && hadCost,
      ...(costSplit === 'manual' && (parsed.cost != null || hadCost)
        ? {
            payer_ids: pairs.map((row) => row.user_id),
            payer_shares: pairs.map((row) => row.share),
          }
        : {}),
    };
  };

  const handleConfirm = () => {
    if (!performerType) {
      showError('Укажите, кем проведено ТО');
      return;
    }
    if (
      (performerType === 'technician' || performerType === 'organization') &&
      !performerName.trim()
    ) {
      showError(
        performerType === 'organization'
          ? 'Укажите наименование организации'
          : 'Укажите ФИО техника'
      );
      return;
    }
    const mechanic = formatPerformerLabel(performerType, performerName);
    const extra = extras();
    if (!extra) return;
    if (starterWithoutFlights) {
      if (!doneDate) {
        showError('Укажите дату ТО');
        return;
      }
      if (!reason) {
        showError('Укажите причину проведения ТО');
        return;
      }
      onConfirm({
        done_date: doneDate,
        reason,
        mechanic_name: mechanic,
        ...extra,
      });
      return;
    }
    if (!flightId || !selected) {
      showError('Выберите полёт, после которого провести ТО');
      return;
    }
    if (motoOf(selected) <= lastDoneHobbs && selected.id !== initialFlightId) {
      showError('Выберите полёт с наработкой больше предыдущего ТО');
      return;
    }
    if (
      maxHobbs != null &&
      motoOf(selected) >= maxHobbs &&
      selected.id !== initialFlightId
    ) {
      showError(`Выберите полёт с наработкой меньше следующего ТО (${maxHobbs} м/ч)`);
      return;
    }
    if (!doneDate) {
      showError('Укажите дату ТО');
      return;
    }
    if (title === 'ТО-100' && !reason) {
      showError('Укажите причину проведения ТО');
      return;
    }
    onConfirm({
      after_flight_id: Number(flightId),
      done_date: doneDate,
      mechanic_name: mechanic,
      ...(title === 'ТО-100' && reason ? { reason } : {}),
      ...extra,
    });
  };

  const fieldsDisabled = !starterWithoutFlights && eligibleFlights.length === 0;

  return (
    <ModalPortal>
    <div className="modal-overlay" onClick={onClose}>
      {feedbackNode}
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>{mode === 'edit' ? `Изменить ${title}` : `Провести ${title}`}</h3>
        <div className="modal-body">
          {starterWithoutFlights ? (
            <p className="perform-to-hint">
              Для нового борта стартовое ТО-100 проводится без полётов — фиксируется
              нулевая наработка.
            </p>
          ) : (
            <p className="perform-to-hint">
              {mode === 'edit'
                ? maxHobbs != null
                  ? `Перепривяжите ТО к полёту после ${lastDoneHobbs} м/ч и до следующего ТО (${maxHobbs} м/ч). Наработка и процентовка пересчитаются.`
                  : 'Можно перепривязать ТО к другому полёту. Наработка и процентовка пересчитаются.'
                : 'Выберите полёт, после которого было сделано ТО. Можно указать любой уже совершённый полёт между предыдущим ТО и текущей наработкой (моточасы).'}
            </p>
          )}

          <label>Отметка начала периода</label>
          <p>{lastDoneHobbs} м/ч</p>

          <label>Налёт борта сейчас</label>
          <p>{tipMoto} м/ч</p>

          {!starterWithoutFlights ? (
            <>
              <label>После полёта</label>
              {loading ? (
                <p>Загрузка полётов...</p>
              ) : loadFailed ? (
                <p className="perform-to-empty">Не удалось загрузить список полётов.</p>
              ) : eligibleFlights.length === 0 ? (
                <p className="perform-to-empty">
                  {maxHobbs != null
                    ? `Нет полётов между ${lastDoneHobbs} м/ч и следующим ТО (${maxHobbs} м/ч).`
                    : `Нет полётов после отметки ${lastDoneHobbs} м/ч.`}
                  {maxHobbs == null && tipMoto <= lastDoneHobbs
                    ? ` Добавьте полёт с моточасами больше ${lastDoneHobbs} м/ч — сейчас ${tipMoto} м/ч (возможно, полёты после ТО были удалены).`
                    : ''}
                </p>
              ) : (
                <select
                  value={flightId}
                  onChange={(e) => handleFlightChange(Number(e.target.value))}
                >
                  {eligibleFlights.map((f) => (
                    <option key={f.id} value={f.id}>
                      {DateFormat(String(f.date))} · {f.pilot} · Моточасы {motoOf(f)} ·
                      Тахометр {flightTachValue(f)} · {flightDurationMh(f)} {MH_UNIT}
                    </option>
                  ))}
                </select>
              )}

              {selected && (
                <>
                  <label>Налёт на момент ТО</label>
                  <p>{motoOf(selected)} м/ч</p>
                  {motoOf(selected) !== tipMoto ? (
                    <p className="perform-to-hint">
                      Сейчас в журнале {tipMoto} м/ч. В форму запишется налёт
                      после выбранного полёта — {motoOf(selected)} м/ч.
                    </p>
                  ) : null}
                </>
              )}
            </>
          ) : (
            <>
              <label>Налёт на момент ТО</label>
              <p>0 м/ч</p>
            </>
          )}

          <label>Дата ТО</label>
          <input
            type="date"
            value={doneDate}
            onChange={(e) => setDoneDate(e.target.value)}
            disabled={fieldsDisabled}
          />

          <label>Кем проведено</label>
          <select
            value={performerType}
            onChange={(e) =>
              setPerformerType(e.target.value as MaintenancePerformerType)
            }
            disabled={fieldsDisabled}
          >
            <option value="">Не указано</option>
            <option value="technician">Техник</option>
            <option value="organization">Организация</option>
            <option value="self">Своими силами</option>
          </select>
          {performerType ? (
            <>
              <label>{performerNamePlaceholder(performerType)}</label>
              <input
                type="text"
                value={performerName}
                onChange={(e) => setPerformerName(e.target.value)}
                maxLength={80}
                placeholder={performerNamePlaceholder(performerType)}
                disabled={fieldsDisabled}
              />
            </>
          ) : null}

          {title === 'ТО-100' && (
            <>
              <label className="perform-to-reason-label">Причина проведения</label>
              <select
                className="perform-to-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value as MaintenanceReason)}
                disabled={fieldsDisabled}
              >
                {starterWithoutFlights ? (
                  <option value="initial">Стартовое ТО</option>
                ) : null}
                <option value="hours">Прошло 100 м/ч</option>
                <option value="year">Прошёл год</option>
              </select>
            </>
          )}

          <MaintenanceCostFields
            cost={cost}
            costSplit={costSplit}
            manualShares={manualShares}
            owners={owners}
            record={initialRecord}
            radioName={mode === 'edit' ? 'to-edit-cost-split' : 'to-cost-split'}
            onCostChange={setCost}
            onCostSplitChange={setCostSplit}
            onManualSharesChange={setManualShares}
          />

          <label>Инфо</label>
          <textarea
            className="perform-to-info"
            value={info}
            onChange={(e) => setInfo(e.target.value)}
            placeholder="Комментарий или заметки по этому ТО"
            rows={4}
          />
        </div>
        <div className="modal-actions">
          <button type="button" className="btn-cancel" onClick={onClose} disabled={submitting}>
            Отмена
          </button>
          <button
            type="button"
            className="btn-save"
            onClick={handleConfirm}
            disabled={!canSubmit}
          >
            {submitting
              ? mode === 'edit'
                ? 'Сохранение...'
                : 'Проведение...'
              : mode === 'edit'
                ? 'Сохранить'
                : `Провести ${title}`}
          </button>
        </div>
      </div>
    </div>
    </ModalPortal>
  );
};

export default PerformMaintenanceModal;
